import { useTranslation } from 'react-i18next'
import { FC, useMemo } from 'react'

import {
  MRT_Table as MRTTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';

type PolicyRecording = {
  contract_number: string
  datetime: string
  filename: string
  format: string
  size: string
  url: string
}

type ListUploadedFilesBlockProps = {
  policyRecordings: {
    id: string
    recordings: PolicyRecording[]
  }
}

export const ListUploadedFilesBlock: FC<ListUploadedFilesBlockProps> = ({ policyRecordings }) => {
  const { t } = useTranslation()
  const TRANSLATION_BASE_KEY = 'userArea.profile.actions.uploadedFiles'

  const getFileType = (format: string): string => {
    const audioFormats = ['mp3', 'wav', 'aac', 'm4a']
    const videoFormats = ['mp4', 'mov', 'avi', 'wmv']
    
    format = format.toLowerCase()
    if (audioFormats.includes(format)) return 'Audio'
    if (videoFormats.includes(format)) return 'Vídeo'
    return format
  }

  const formatDateTime = (datetime: string) => {
    const date = new Date(datetime);
    const formattedDate = date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const formattedTime = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return `${formattedDate}\n${formattedTime}`;
  }

  const parsePolicyRecordings = (data: any): PolicyRecording[] => {
    if (!data || !Array.isArray(data) || data.length < 1) return []

    return data.map((item: any) => {
      const datetime = formatDateTime(item.datetime);
      const sizeInMB = (parseInt(item.size) / (1024 * 1024)).toFixed(2) + ' MB';

      return {
        contract_number: item.contractNumber,
        datetime: datetime,
        filename: item.filename,
        format: getFileType(item.format),
        size: sizeInMB,
        url: item.url,
      }
    })
  }

  const dataTable: PolicyRecording[] = parsePolicyRecordings(policyRecordings?.recordings || null)

  const columns = useMemo<MRT_ColumnDef<PolicyRecording>[]>(
    () => [
      {
        accessorKey: 'contract_number',
        header: 'Nº de contrato',
        size: 100,
      },
      {
        accessorKey: 'datetime',
        header: 'Fecha y hora',
        size: 100,
      },
      {
        accessorKey: 'filename',
        header: 'Nombre archivo',
        size: 200,
      },
      {
        accessorKey: 'format',
        header: 'Formato',
        size: 100,
      },
      {
        accessorKey: 'size',
        header: 'Tamaño',
        size: 100,
      },
    ],
    [],
  )

  const table = useMaterialReactTable({ 
    columns,
    data: dataTable,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    muiTableBodyRowProps: { hover: false },
    muiTableProps: {
      sx: {
        border: 'none',
        caption: {
          captionSide: 'top',
        },
        boxShadow: 'none',
      },
    },
    muiTableHeadProps: {
      sx: {
        marginBottom: '24px',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontFamily: 'Aeonik',
        fontSize: '16px',
        fontWeight: '500',
        lineHeight: '24px',
        padding: 'none',
        height: '44px',
        border: 'none',
        borderBottom: '24px solid transparent',
        color: '#333333',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        border: 'none',
        height: '68px',
        whiteSpace: 'pre-line',
        verticalAlign: 'middle',
        fontFamily: 'Aeonik',
        fontSize: '16px',
        fontWeight: '400',
        lineHeight: '24px',
        color: '#333333',
      },
    },
  })

  if (!policyRecordings) {
    return false
  }

  return (
    <div className="flex flex-col mb-6">
      <div className="text-primary text-xl font-semibold mb-6">
        {t(`${TRANSLATION_BASE_KEY}.title`)}
      </div>
      {policyRecordings?.recordings && policyRecordings.recordings?.length > 0 ? (
        <div className="text-dark-gray font-normal text-base">
          <MRTTable table={table} />
        </div>
      ) : (
        <div className="text-dark-gray font-normal text-base h-11 content-center">
          {t(`${TRANSLATION_BASE_KEY}.noResulst`)}
        </div>
      )}
    </div>
  )
}
